import * as React from "react";

// ---------- Portal context for shadow DOM support ----------
export type PortalContainer = ShadowRoot | HTMLElement | undefined;

type PortalCtxValue = {
  container: PortalContainer;
  setContainer: (el: PortalContainer) => void;
};

const PortalCtx = React.createContext<PortalCtxValue | null>(null);

export function usePortalContext(componentName: string) {
  const ctx = React.useContext(PortalCtx);
  if (!ctx) {
    throw new Error(
      `${componentName} components must be used within a component that provides portal context`
    );
  }
  return ctx;
}

// Compose refs helper
export function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") ref(node);
      else {
        try {
          // @ts-ignore
          ref.current = node;
        } catch {}
      }
    }
  };
}

export function detectContainerFrom(el: HTMLElement | null): PortalContainer {
  if (!el) return undefined;
  const root = el.getRootNode();
  return root instanceof ShadowRoot ? root : undefined; // undefined => default body
}

// Provider component
interface PortalProviderProps {
  children: React.ReactNode;
}

export function PortalProvider({ children }: PortalProviderProps) {
  const [container, setContainer] = React.useState<PortalContainer>(undefined);
  const value = React.useMemo(() => ({ container, setContainer }), [container]);

  return <PortalCtx.Provider value={value}>{children}</PortalCtx.Provider>;
}

// Helper component to detect shadow DOM container from any rendered element
export function PortalDetector() {
  const { setContainer } = React.useContext(PortalCtx) || {};
  const detectorRef = React.useRef<HTMLSpanElement>(null);

  React.useLayoutEffect(() => {
    if (detectorRef.current && setContainer) {
      setContainer(detectContainerFrom(detectorRef.current));
    }
  }, [setContainer]);

  return <span ref={detectorRef} style={{ display: "none" }} />;
}
